import { useMemo, useState, useRef, useEffect } from 'react';
import { ColumnDef, DataTableProps } from './DataTable.types';

function MultiSelectFilter({ options, selected, onChange }: {
    options: { value: string; label: string }[];
    selected: string[];
    onChange: (values: string[]) => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    const toggle = (val: string) => {
        const next = selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val];
        onChange(next);
    };
    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(!open)}
                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 text-left truncate flex items-center justify-between"
            >
                <span>{selected.length ? `${selected.length} selecionado(s)` : 'Filtrar...'}</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 shrink-0 text-gray-400"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
            </button>
            {open && (
                <div className="absolute z-50 mt-1 min-w-[160px] bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded shadow-lg max-h-48 overflow-y-auto">
                    {options.map(opt => (
                        <label key={opt.value} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer text-xs">
                            <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} className="w-3 h-3" />
                            {opt.label}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function DebouncedInput({ value, onChange, placeholder, className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
    const [local, setLocal] = useState(value);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => { setLocal(value); }, [value]);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setLocal(v);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onChange(v), 300);
    };
    return <input type="text" value={local} onChange={handleChange} placeholder={placeholder} className={className} />;
}

function DataTable<T>({
    columns,
    data,
    rowKey,
    filters,
    onFiltersChange,
    sortField,
    sortOrder,
    onSort,
    showFilters = true,
    hiddenColumns,
    selectedRows,
    onSelectRow,
    onSelectAll,
    showCheckbox = false,
    emptyMessage = 'Nenhum registro encontrado...',
    height,
}: DataTableProps<T>) {
    const visibleColumns = useMemo(
        () => columns.filter(c => !hiddenColumns?.has(c.field)),
        [columns, hiddenColumns],
    );

    const stickyOffsets = useMemo(() => {
        const offsets: Record<string, number> = {};
        let left = 0;
        if (showCheckbox) {
            offsets['__checkbox'] = 0;
            left = 50;
        }
        for (const col of visibleColumns) {
            if (!col.sticky) break;
            offsets[col.id] = left;
            left += parseInt(col.width) || 0;
        }
        return offsets;
    }, [visibleColumns, showCheckbox]);

    const getRowBg = (index: number) =>
        index % 2 === 0
            ? 'bg-slate-100 dark:bg-slate-700'
            : 'bg-white dark:bg-slate-800';

    const stickyCell = (colId: string, bg: string) => {
        if (!(colId in stickyOffsets)) return '';
        return `sticky z-[5] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`;
    };

    const stickyCellStyle = (colId: string) => {
        if (!(colId in stickyOffsets)) return {};
        return { left: stickyOffsets[colId] };
    };

    const stickyHeader = (colId: string) => {
        if (!(colId in stickyOffsets)) return '';
        return `sticky z-[25] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`;
    };

    const stickyHeaderStyle = (colId: string) => {
        if (!(colId in stickyOffsets)) return {};
        return { left: stickyOffsets[colId] };
    };

    const allSelected = showCheckbox && selectedRows && data.length > 0 && selectedRows.size === data.length;

    const renderHeaderCell = (col: ColumnDef<T>) => {
        if (col.renderHeader) return col.renderHeader();

        return (
            <div className="flex flex-col gap-1">
                <div
                    className={`flex items-center gap-1 ${col.canSort !== false ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400' : ''}`}
                    onClick={() => col.canSort !== false && onSort(col.field)}
                >
                    <span className="font-semibold text-xs whitespace-nowrap">{col.title}</span>
                    {sortField === col.field && (
                        <span className="text-xs">{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                </div>
                {showFilters && col.canSort !== false && (
                    col.filterType === 'multiselect' && col.filterOptions ? (
                        <MultiSelectFilter
                            options={col.filterOptions}
                            selected={(filters[col.field] || '').split(';').filter(Boolean)}
                            onChange={(vals) => onFiltersChange({ ...filters, [col.field]: vals.join(';') })}
                        />
                    ) : col.filterType === 'select' && col.filterOptions ? (
                        <select
                            value={filters[col.field] || ''}
                            onChange={e => onFiltersChange({ ...filters, [col.field]: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:border-blue-400 focus:outline-none appearance-none"
                        >
                            <option value="">Todos</option>
                            {col.filterOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    ) : (
                    <div className="flex items-center gap-1">
                        <DebouncedInput
                            value={filters[col.field] || ''}
                            onChange={v => onFiltersChange({ ...filters, [col.field]: v })}
                            placeholder="Filtrar..."
                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:border-blue-400 dark:focus:border-blue-400 focus:outline-none"
                        />
                        {col.filterType === 'range' && (
                            <span className="text-gray-400 dark:text-gray-500 cursor-help shrink-0" title="Filtros: 2000 (exato), >2k (maior), <5k (menor), 2k:5k (entre), >=1m, :10k (até)&#10;Atalhos: k=mil, m=milhão">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                                </svg>
                            </span>
                        )}
                    </div>
                    )
                )}
            </div>
        );
    };

    const defaultRenderCell = (col: ColumnDef<T>, row: T) => {
        const value = (row as Record<string, unknown>)[col.field];
        return <span className="dark:text-gray-200">{value != null ? String(value) : '-'}</span>;
    };

    return (
        <div
            style={height ? { height } : undefined}
            className={`overflow-auto border border-gray-200 dark:border-slate-600 rounded datatable-scroll ${!height ? 'flex-1 min-h-0' : ''}`}
        >
            <style>{`
                .datatable-scroll::-webkit-scrollbar { width: 12px; height: 12px; }
                .datatable-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
                .dark .datatable-scroll::-webkit-scrollbar-track { background: #1e293b; }
                .datatable-scroll::-webkit-scrollbar-thumb { background: #888; border-radius: 6px; }
                .datatable-scroll::-webkit-scrollbar-thumb:hover { background: #555; }
            `}</style>

            <table className="text-sm" style={{ tableLayout: 'fixed', width: 'max-content' }}>
                <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0 z-20">
                    <tr>
                        {showCheckbox && (
                            <th
                                className={`px-4 py-2 bg-gray-50 dark:bg-slate-700 ${stickyHeader('__checkbox')}`}
                                style={{ width: 50, minWidth: 50, maxWidth: 50, ...stickyHeaderStyle('__checkbox') }}
                            >
                                <div className="flex items-center justify-center">
                                    <input
                                        type="checkbox"
                                        checked={!!allSelected}
                                        onChange={() => onSelectAll?.()}
                                        className="w-4 h-4 cursor-pointer"
                                    />
                                </div>
                            </th>
                        )}
                        {visibleColumns.map(col => (
                            <th
                                key={col.id}
                                className={`px-4 py-2 text-left bg-gray-50 dark:bg-slate-700 ${stickyHeader(col.id)}`}
                                style={{
                                    minWidth: col.width,
                                    width: col.width,
                                    maxWidth: col.width,
                                    ...stickyHeaderStyle(col.id),
                                }}
                            >
                                {renderHeaderCell(col)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody key={data.length} className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-600">
                    {data.length === 0 ? (
                        <tr>
                            <td
                                colSpan={visibleColumns.length + (showCheckbox ? 1 : 0)}
                                className="py-2 px-2 text-center"
                            >
                                <div className="text-2xl font-bold text-gray-500 dark:text-gray-400 mt-4">
                                    {emptyMessage}
                                </div>
                            </td>
                        </tr>
                    ) : (
                        data.map((row, index) => {
                            const bg = getRowBg(index);
                            const key = rowKey(row);
                            return (
                                <tr
                                    key={key}
                                    className={`group ${bg} hover:bg-slate-300 dark:hover:bg-slate-600 hover:shadow-md border-b border-gray-300 dark:border-gray-600`}
                                >
                                    {showCheckbox && (
                                        <td
                                            className={`py-2 px-4 ${bg} group-hover:!bg-slate-300 dark:group-hover:!bg-slate-600 ${stickyCell('__checkbox', bg)}`}
                                            style={{ width: 50, minWidth: 50, maxWidth: 50, ...stickyCellStyle('__checkbox') }}
                                        >
                                            <div className="flex items-center justify-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRows?.has(key) ?? false}
                                                    onChange={() => onSelectRow?.(key)}
                                                    className="w-4 h-4 cursor-pointer"
                                                />
                                            </div>
                                        </td>
                                    )}
                                    {visibleColumns.map(col => {
                                        const cellBg = col.bgClassName ? col.bgClassName(row, bg) : bg;
                                        const alignClass =
                                            col.align === 'right' ? 'text-right' :
                                            col.align === 'center' ? 'text-center' : '';
                                        return (
                                            <td
                                                key={col.id}
                                                className={`py-2 px-4 ${cellBg} group-hover:!bg-slate-300 dark:group-hover:!bg-slate-600 ${alignClass} ${stickyCell(col.id, cellBg)}`}
                                                style={{
                                                    minWidth: col.width,
                                                    width: col.width,
                                                    maxWidth: col.width,
                                                    ...stickyCellStyle(col.id),
                                                }}
                                            >
                                                {col.renderCell
                                                    ? col.renderCell(row, index)
                                                    : defaultRenderCell(col, row)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default DataTable;
